import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { UserSearchResult } from '../../../api/users';
import { startImpersonation } from './impersonation-api';
import { saveReturnPath } from './impersonation-storage';
import { enqueueNotification } from '../../../store/notification/action';
import styles from './impersonation-confirm-dialog.module.scss';

interface Props {
  target: UserSearchResult | null;
  onCancel: () => void;
}

const ImpersonationConfirmDialog = ({ target, onCancel }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  if (!target) {
    return null;
  }

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      saveReturnPath(location.pathname + location.search);
      await startImpersonation(target.wwid);
      window.location = window.location.origin as any;
    } catch {
      setSubmitting(false);
      dispatch(
        enqueueNotification(
          t('impersonation.startError', 'Could not start the impersonation session'),
        ),
      );
    }
  };

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="xs">
      <DialogTitle>{t('impersonation.confirmTitle', 'Confirm impersonation')}</DialogTitle>
      <DialogContent>
        <div className={styles.field}>
          <span className={styles.label}>Name</span>
          <strong>{target.name}</strong>
        </div>
        <div className={styles.field}>
          <span className={styles.label}>WWID</span>
          <span>{target.wwid}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Email</span>
          <span>{target.email}</span>
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Role</span>
          <span>{target.role}</span>
        </div>
        <div className={styles.warning}>
          {t(
            'impersonation.confirmWarning',
            'You are about to view Campaign Planner as this user. The session is read-only — no changes can be made.',
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="text" className={styles.cancel} onClick={onCancel} disabled={submitting}>
          {t('impersonation.cancelButton', 'Cancel')}
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          {t('impersonation.confirmButton', 'Impersonate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImpersonationConfirmDialog;
